import React, { useEffect, useState, useCallback } from 'react';
import api from '../../api';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import {
  BarChart3, Eye, Users, TrendingUp, TrendingDown, Minus, RefreshCw, Calendar
} from 'lucide-react';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend
} from 'recharts';
import { cn } from '../../lib/utils';

const PRO_LABEL = {
  guest: 'Visiteurs anonymes',
  eleve: 'Élèves',
  enseignant: 'Enseignants',
  admin: 'Admins',
};

function pctDelta(current, previous) {
  if (!previous) return current > 0 ? 100 : 0;
  return Math.round(((current - previous) / previous) * 100);
}

function DeltaBadge({ delta }) {
  if (delta === 0) {
    return (
      <span className="inline-flex items-center gap-0.5 text-[11px] font-semibold text-muted-foreground">
        <Minus className="w-3 h-3" /> 0%
      </span>
    );
  }
  const positive = delta > 0;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-0.5 text-[11px] font-semibold',
        positive ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'
      )}
    >
      {positive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
      {positive ? '+' : ''}{delta}%
    </span>
  );
}

function StatCard({ label, value, sub, delta, icon: Icon, color = 'primary' }) {
  return (
    <div className="mc-card p-4 sm:p-5">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">{label}</div>
          <div className="text-2xl sm:text-3xl font-black mt-0.5 tabular-nums">{(value ?? 0).toLocaleString('fr-FR')}</div>
          {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
        </div>
        <span
          className={cn(
            'w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0',
            color === 'primary' ? 'bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]' :
            color === 'emerald' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' :
            'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
          )}
        >
          <Icon className="w-4 h-4" />
        </span>
      </div>
      {delta !== undefined && (
        <div className="mt-2"><DeltaBadge delta={delta} /> <span className="text-[10px] text-muted-foreground ml-1">vs période préc.</span></div>
      )}
    </div>
  );
}

export default function AdminAnalytics() {
  const [overview, setOverview] = useState(null);
  const [timeline, setTimeline] = useState({ series: [] });
  const [topPages, setTopPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [o, tl, tp] = await Promise.all([
        api.getAnalyticsOverview(),
        api.getAnalyticsTimeline(days),
        api.getAnalyticsTopPages(10, days),
      ]);
      setOverview(o);
      setTimeline(tl);
      setTopPages(tp.pages || []);
    } catch (e) {
      toast.error(e.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const o = overview;

  // Préparer les données du graphe (dates plus lisibles)
  const chartData = (timeline.series || []).map(d => {
    const dt = new Date(d.date);
    return {
      ...d,
      label: dt.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }),
    };
  });

  return (
    <div className="space-y-5 max-w-6xl" data-testid="admin-analytics-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-[hsl(var(--primary))] mb-1">
            <BarChart3 className="w-3.5 h-3.5" /> Back-office admin
          </div>
          <h1 className="text-2xl sm:text-3xl font-black tracking-tight">Statistiques de fréquentation</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Suivi en temps réel des visites de votre plateforme. Données conservées 12 mois.
          </p>
        </div>
        <Button variant="outline" size="sm" className="rounded-full gap-1.5" onClick={load} data-testid="analytics-refresh">
          <RefreshCw className="w-4 h-4" /> Rafraîchir
        </Button>
      </div>

      {/* Cartes principales */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard
          label="Aujourd'hui"
          value={o?.today.views}
          sub={`${o?.today.unique_visitors || 0} visiteur${(o?.today.unique_visitors || 0) > 1 ? 's' : ''}`}
          delta={o ? pctDelta(o.today.views, o.yesterday.views) : 0}
          icon={Eye}
          color="primary"
        />
        <StatCard
          label="Cette semaine"
          value={o?.this_week.views}
          sub={`${o?.this_week.unique_visitors || 0} visiteurs uniques`}
          delta={o ? pctDelta(o.this_week.views, o.last_week.views) : 0}
          icon={Calendar}
          color="emerald"
        />
        <StatCard
          label="Ce mois"
          value={o?.this_month.views}
          sub={`${o?.this_month.unique_visitors || 0} visiteurs uniques`}
          delta={o ? pctDelta(o.this_month.views, o.last_month.views) : 0}
          icon={TrendingUp}
          color="blue"
        />
        <StatCard
          label="Total"
          value={o?.all_time.views}
          sub={`${o?.all_time.unique_visitors || 0} visiteurs uniques`}
          icon={Users}
          color="primary"
        />
      </div>

      {/* Graphique évolution */}
      <div className="mc-card p-4 sm:p-5">
        <div className="flex items-start justify-between flex-wrap gap-2 mb-4">
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Évolution</div>
            <h2 className="text-lg font-black">Vues et visiteurs uniques</h2>
          </div>
          <div className="flex gap-1 flex-wrap">
            {[7, 14, 30, 90].map(d => (
              <Button
                key={d}
                size="sm"
                variant={days === d ? 'default' : 'outline'}
                onClick={() => setDays(d)}
                className="rounded-full h-7 text-[11px]"
                data-testid={`analytics-period-${d}`}
              >
                {d}j
              </Button>
            ))}
          </div>
        </div>
        <div className="h-72 w-full">
          {loading ? (
            <div className="h-full w-full flex items-center justify-center text-muted-foreground text-sm">Chargement...</div>
          ) : chartData.length === 0 ? (
            <div className="h-full w-full flex items-center justify-center text-muted-foreground text-sm">Aucune donnée</div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 5, right: 12, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorViews" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorVisitors" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10b981" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" allowDecimals={false} />
                <Tooltip
                  contentStyle={{
                    background: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: 12,
                    fontSize: 12,
                  }}
                  labelStyle={{ fontWeight: 700 }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Area type="monotone" dataKey="views" name="Vues" stroke="hsl(var(--primary))" strokeWidth={2} fill="url(#colorViews)" />
                <Area type="monotone" dataKey="unique_visitors" name="Visiteurs uniques" stroke="#10b981" strokeWidth={2} fill="url(#colorVisitors)" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-3">
        {/* Top pages */}
        <div className="mc-card p-4 sm:p-5">
          <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Top pages · {days}j</div>
          <h2 className="text-lg font-black mb-3">Pages les plus visitées</h2>
          {topPages.length === 0 ? (
            <div className="text-sm text-muted-foreground py-6 text-center">Aucune donnée pour cette période</div>
          ) : (
            <div className="space-y-2">
              {topPages.map((p, i) => {
                const max = topPages[0].views || 1;
                const pct = Math.round((p.views / max) * 100);
                return (
                  <div key={p.path} className="space-y-1" data-testid={`top-page-${i}`}>
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-mono truncate flex-1 mr-2">{p.path}</span>
                      <span className="font-bold tabular-nums flex-shrink-0">{p.views.toLocaleString('fr-FR')}</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-[hsl(var(--panel))] overflow-hidden">
                      <div
                        className="h-full bg-[hsl(var(--primary))] rounded-full transition-all"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <div className="text-[10px] text-muted-foreground">{p.unique_visitors} visiteur{p.unique_visitors > 1 ? 's' : ''} unique{p.unique_visitors > 1 ? 's' : ''}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Répartition par rôle */}
        <div className="mc-card p-4 sm:p-5">
          <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Ce mois</div>
          <h2 className="text-lg font-black mb-3">Répartition par profil</h2>
          {(o?.by_role_this_month || []).length === 0 ? (
            <div className="text-sm text-muted-foreground py-6 text-center">Aucune donnée ce mois</div>
          ) : (
            <div className="space-y-3">
              {(o?.by_role_this_month || []).map((r) => {
                const totalRoleViews = (o?.by_role_this_month || []).reduce((s, x) => s + x.views, 0) || 1;
                const pct = Math.round((r.views / totalRoleViews) * 100);
                return (
                  <div key={r.role} className="space-y-1" data-testid={`role-stat-${r.role}`}>
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-semibold">{PRO_LABEL[r.role] || r.role}</span>
                      <span className="text-muted-foreground tabular-nums">{r.views.toLocaleString('fr-FR')} vues · {r.unique_visitors} pers.</span>
                    </div>
                    <div className="h-2 rounded-full bg-[hsl(var(--panel))] overflow-hidden">
                      <div
                        className={cn(
                          'h-full rounded-full transition-all',
                          r.role === 'guest' ? 'bg-blue-500' :
                          r.role === 'eleve' ? 'bg-emerald-500' :
                          r.role === 'enseignant' ? 'bg-amber-500' :
                          'bg-rose-500'
                        )}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
